import axios from 'axios'
import type { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { tokenStore } from './tokenStore'
import { refreshTokenStore } from './refreshTokenStore'
import { SESSION_REFRESH_TOKEN_KEY, SESSION_TOKEN_KEY } from './sessionKeys'
import { authActions } from '../contexts/AuthContext'
import type { RefreshData } from '../types/api'
import { unwrapSuccessPayload } from './apiEnvelope'

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL ?? '',
})

// ── Request: attach Bearer token (refresh uses body only, no Bearer) ───────

axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const isRefresh = config.url?.includes('/auth/refresh')

  if (isRefresh && config.headers) {
    delete config.headers.Authorization
    return config
  }

  const token = tokenStore.get()

  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }

  return config
})

// ── Response: 401 → refresh → retry, else logout ──────────────────────────

let isRefreshing = false
let pendingQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = []

const processPendingQueue = (error: unknown, token: string | null): void => {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else if (token) {
      resolve(token)
    } else {
      //Do nothing
    }
  })
  pendingQueue = []
}

const persistTokensAfterRefresh = (accessToken: string, nextRefreshToken?: string): void => {
  sessionStorage.setItem(SESSION_TOKEN_KEY, accessToken)
  if (nextRefreshToken) {
    sessionStorage.setItem(SESSION_REFRESH_TOKEN_KEY, nextRefreshToken)
    refreshTokenStore.set(nextRefreshToken)
  }
}

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Don't intercept auth endpoints — let them return their errors directly
    const isAuthEndpoint = originalRequest.url?.includes('/auth/')
    if (error.response?.status !== 401 || originalRequest._retry || isAuthEndpoint) {
      return Promise.reject(error)
    }

    const storedRefresh = refreshTokenStore.get()
    if (!storedRefresh) {
      authActions.logout()
      window.location.href = '/login'

      return Promise.reject(error)
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({
          resolve: (token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`
            }

            resolve(axiosInstance(originalRequest))
          },
          reject,
        })
      })
    }

    originalRequest._retry = true
    isRefreshing = true

    try {
      const { data } = await axiosInstance.post<RefreshData>('/api/v1/auth/refresh', {
        refreshToken: storedRefresh,
      })

      const newToken = data.accessToken
      tokenStore.set(newToken)
      persistTokensAfterRefresh(newToken, data.refreshToken)

      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`
      }

      processPendingQueue(null, newToken)

      return axiosInstance(originalRequest)
    } catch (refreshError) {
      processPendingQueue(refreshError, null)
      authActions.logout()
      window.location.href = '/login'

      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  },
)

// Unwrap `{ success, data, message }` → inner `data` (runs first on success; registered last)
axiosInstance.interceptors.response.use((response) => {
  const payload = response.data
  if (payload !== null && typeof payload === 'object' && !(payload instanceof Blob)) {
    response.data = unwrapSuccessPayload(payload)
  }

  return response
})

export default axiosInstance
